import { BCCard, BCCardGrid, BCScrollbars } from '@bimser/components';
import { FormItemTypes, IDictionary, createGuid } from '@bimser/core';
import classNames from 'classnames/bind';
import { Liquid } from 'liquidjs';
import React, { JSX } from 'react';
import {
	IDataSource,
	IDataSourceResponse,
	IOnLoadArgs,
	IPropertyField
} from '../../../../entities/common';
import { getDataSourceData, getResultData } from '../../../../helper';
import VisualControl from '../../base/VisualControl';
import { IDataLoadEventArgs } from '../../entities/EventArgs';
import Styles from './assets/styles.scss';
import { ICardItem, ICardProperties } from './entities';

const liquidEngine = new Liquid();
const cx = classNames.bind(Styles);

export default class Card extends VisualControl {
	data: IDictionary<any>[];
	dataSource: IDataSource;
	wrapperDirection: 'horizontal' | 'vertical';
	items: Array<ICardItem>;
	titleExpr: string;
	valueExprs: string[];

	__dataSourceLoadedAtLeastOnce?: boolean;

	constructor() {
		super();

		this.controlType = FormItemTypes.EControlTypes.Card;
		this.caption.position = 'top';
		this._hideCommonLoadingOverlay = true;
		this.dataSource = {
			name: null,
			runAtServer: false,
			parameters: [],
			useClientCachedData: true
		};
		this.wrapperDirection = 'vertical';
		this.items = [];
		this.titleExpr = '';
		this.valueExprs = [];
		this.data = [];
	}

	__loadProperties(data: ICardProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.dataSource = data.dataSource;
		this.__proxyChanged.wrapperDirection = data.wrapperDirection;
		this.__proxyChanged.items = data.items;
		this.__proxyChanged.titleExpr = data.titleExpr;
		this.__proxyChanged.valueExprs = data.valueExprs;
		this.__proxyChanged.data = data.data;
	}

	__onLoad({ externalData, reload, forceRefresh }: IOnLoadArgs) {
		if (!this.__isInitialized && this.dataSource?.parameters?.length && !reload) {
			return;
		}

		if (
			this.__form.serviceUrl &&
			this.__form.displayMode !== 'preview' &&
			this.dataSource?.name &&
			!this.dataSource.runAtServer
		) {
			this.__proxyChanging.loading = true;
			getDataSourceData({
				dataSource: this.dataSource,
				projectName: this.__form.projectName,
				clientCacheId: this.__form.__windowId,
				fieldParams: externalData?.fieldParams,
				reload,
				forceRefresh,
				projectVersion: this.__form.packageVersion
			})
				.then(response => {
					const data = response ? getResultData(response.data) : null;

					if (data?.length) {
						const tId = this.__form.beginTransaction();

						this.emit('event:OnDataLoading', { data } as IDataLoadEventArgs).then(
							result => {
								let newData = result[0];

								const hasDataLoadingEvent = this.clientEvents.find(
									i => i.name === 'OnDataLoading'
								);
								if (!hasDataLoadingEvent) {
									newData = data;
								}

								this.__proxyChanging.data = newData;

								this.__generateCardItems(newData, response.data).then(
									cardItemsResponse => {
										this.__proxyChanging.items = cardItemsResponse.items;

										this.__form.endTransaction(tId).then(() => {
											this.__proxyChanging.loading = false;
											this.__dataSourceLoadedAtLeastOnce = true;

											this.emit('event:OnDataLoad', {
												data: newData
											} as IDataLoadEventArgs);
										});
									}
								);
							}
						);
					} else {
						this.__proxyChanging.data = [];
						this.__proxyChanging.items = [];
						this.__proxyChanging.loading = false;
					}
				})
				.catch(() => {
					this.__proxyChanging.data = [];
					this.__proxyChanging.items = [];
					this.__proxyChanging.loading = false;
				});
		}
	}

	private __generateCardItems(cardItems: any[], data: IDataSourceResponse) {
		return new Promise<{ items: Array<ICardItem> }>(resolve => {
			const items: Array<ICardItem> = cardItems.map(item => {
				const title = this.titleExpr.length
					? liquidEngine.parseAndRenderSync(`{{ ${this.titleExpr} }}`, item)
					: '-';

				const resultItem: ICardItem = {
					key: createGuid(),
					title,
					values: this.valueExprs.map(valueExpr => ({
						caption:
							data.structure?.columns?.find(i => i.name === valueExpr)?.caption ||
							valueExpr,
						value: item[valueExpr]
					}))
				};

				return resultItem;
			});

			resolve({ items });
		});
	}

	reload(forceRefresh?: boolean) {
		if (this.dataSource?.name) {
			if (this.dataSource?.parameters.length) {
				this.__reloadDataSource(this.dataSource, forceRefresh).catch(() => {
					this.__proxyChanging.loading = false;
				});
			} else {
				this.__onLoad({ reload: true, forceRefresh });
			}
		}
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'wrapperDirection',
				type: 'object',
				items: [
					{
						name: 'vertical',
						type: 'string'
					},
					{
						name: 'horizontal',
						type: 'string'
					}
				]
			}
		];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			dataSource: this.dataSource,
			items: this.items,
			wrapperDirection: this.wrapperDirection,
			valueExprs: this.valueExprs,
			titleExpr: this.titleExpr,
			data: this.data
		};
	}

	__renderItems() {
		// TODO: PREVIEW MODUNDA NASIL ÇALIŞACAK?

		return this.items.map(card => (
			<BCCardGrid
				className={Styles.cardItem}
				key={card.key}
				hoverable={false}
			>
				<div className={Styles.title}>
					<h1>{card.title}</h1>
				</div>
				<ul className={Styles.values}>
					{card.values.map((valueItem, index) => (
						<li key={`${valueItem.value}_${index}`}>
							<p
								className={Styles.cardValue}
								title={valueItem.value}
							>
								{valueItem.value}
							</p>
							<span>{valueItem.caption}</span>
						</li>
					))}
				</ul>
			</BCCardGrid>
		));
	}

	__getStyles() {
		return {
			height: this.style.height || (this.wrapperDirection === 'vertical' ? '252px' : '126px'),
			width: this.wrapperDirection === 'vertical' ? '100%' : 'auto'
		};
	}

	__render(children?: JSX.Element) {
		const cardsclassName = cx({
			[this.wrapperDirection]: true,
			cardsContainer: true
		});

		const style: React.CSSProperties = {
			backgroundColor: this.style?.backgroundColor
		};

		return super.__render(
			<BCCard
				style={style}
				className={cardsclassName}
				loading={this.loading}
			>
				<BCScrollbars
					cssClass={Styles.cardsScrollContainer}
					styles={this.__getStyles()}
				>
					<div className={Styles.scrollWrapper}>{this.__renderItems()}</div>
				</BCScrollbars>
			</BCCard>
		);
	}
}
